import StatusEyebrow from "../components/StatusEyebrow";
import DownloadButton from "../components/DownloadButton";
import { useDownloadFlow } from "../useDownloadFlow";
import { spokenSize, formatExpiry, padPart } from "../format";
import { TrayDownloadIcon, GridIcon, ClockIcon, LockIcon, AlertTriangleIcon } from "../icons";

/*
 * The ready card — one shell for all three "we have your photos" shapes:
 *   multi   — N ready parts, a "Download all" cascade + the per-part list
 *   single  — one .zip, a single hero download
 *   partial — some parts couldn't be built; a warn note + the ready parts, with
 *             the unavailable chunk rendered inline as a disabled row
 * The 90ms cascade + per-button loading→"Saved" affordance live in
 * useDownloadFlow; the disabled row has no ready number so the cascade skips it.
 */
const ReadyView = ({ data, variant, buildPartUrl }) => {
  const {
    channel_name,
    part_count,
    ready_part_count,
    total_size,
    expires_at,
    parts = [],
    failed_count,
    photo_count,
  } = data;

  const flow = useDownloadFlow(buildPartUrl);
  const readyNumbers = parts.filter((p) => p.ready).map((p) => p.part_number);

  // "Download all" is an action (it triggers N part downloads), so it renders as
  // a <button>, not a link — the per-part rows are the real <a href> navigations.
  const handleAll = () => flow.runAll(readyNumbers);

  const renderPartRow = (part) => {
    if (!part.ready) {
      return (
        <li className="part disabled" key={part.part_number}>
          <span className="part-badge" aria-hidden="true">
            {padPart(part.part_number)}
          </span>
          <span className="part-meta">
            <span className="pt">
              Part {part.part_number} of {part_count}
            </span>
            <span className="ps" style={{ color: "var(--warn)" }}>
              Unavailable &middot; some photos missing
            </span>
          </span>
          <span className="btn btn-part" aria-disabled="true" role="button" tabIndex={-1}>
            <span className="lbl">Unavailable</span>
          </span>
        </li>
      );
    }
    return (
      <li className="part" key={part.part_number}>
        <span className="part-badge">{padPart(part.part_number)}</span>
        <span className="part-meta">
          <span className="pt">
            Part {part.part_number} of {part_count}
          </span>
          <span className="ps">{part.size} &middot; .zip</span>
        </span>
        <DownloadButton
          as="a"
          variant="part"
          className="js-dl"
          href={buildPartUrl(part.part_number)}
          state={flow.partStates[part.part_number] || "rest"}
          onClick={() => {
            if (!flow.isCascading()) flow.runSingle(part.part_number);
          }}
          aria-label={`Download part ${part.part_number} of ${part_count}, ${spokenSize(part.size)}`}
          label="Download"
        />
      </li>
    );
  };

  // ---- single: one .zip, one tap ----
  if (variant === "single") {
    const only = readyNumbers[0];
    return (
      <div className="card card-enter">
        <div className="card-pad">
          <StatusEyebrow dot="ok" tone="ok" text="Ready to download" />
          <div>
            <h1>{channel_name}</h1>
            <p className="sub">
              Your gallery is packaged and ready. One tap and it&rsquo;s yours.
            </p>
          </div>
          <div className="meta">
            <span className="chip">
              <TrayDownloadIcon size={13} />
              {total_size} &middot; one .zip
            </span>
            <span className="chip">
              <ClockIcon size={13} />
              Available until {formatExpiry(expires_at)}
            </span>
          </div>
          <DownloadButton
            as="a"
            variant="primary"
            className="js-dl"
            href={buildPartUrl(only)}
            state={flow.partStates[only] || "rest"}
            onClick={() => flow.runSingle(only)}
            aria-label={`Download your photos, ${spokenSize(total_size)} zip file`}
            label="Download your photos"
          />
          <p className="below-btn">
            <LockIcon size={13} />
            Secure link &middot; valid for 90 days
          </p>
        </div>
      </div>
    );
  }

  // ---- partial: some parts couldn't be built ----
  if (variant === "partial") {
    const unavailable = part_count - ready_part_count;
    return (
      <div className="card card-enter">
        <div className="card-pad">
          <StatusEyebrow dot="warn" tone="warn" text="Ready · with a note" />
          <div>
            <h1>{channel_name}</h1>
            <p className="sub">
              Your gallery is packaged into <b>{part_count} parts</b>. A few
              photos couldn&rsquo;t be added this time, so{" "}
              {unavailable === 1 ? "one part is" : `${unavailable} parts are`}{" "}
              unavailable &mdash; but the other <b>{ready_part_count} are ready</b>{" "}
              and good to go.
            </p>
          </div>
          <div className="card-note warn">
            <AlertTriangleIcon size={16} />
            <span>
              <b style={{ color: "inherit" }}>
                {failed_count} of {photo_count} photos couldn&rsquo;t be included.
              </b>{" "}
              This can happen if a photo was still uploading when we packaged the
              gallery. Need them?{" "}
              <a
                href="mailto:help@movebooth.com"
                style={{ color: "inherit", textDecoration: "underline", fontWeight: 600 }}
              >
                Contact support
              </a>{" "}
              and we&rsquo;ll help.
            </span>
          </div>
          <DownloadButton
            as="button"
            type="button"
            variant="primary"
            className="js-dl js-dl-all"
            state={flow.allState}
            onClick={handleAll}
            aria-label={`Download all ${ready_part_count} ready parts, ${spokenSize(total_size)} total`}
            label={`Download all ${ready_part_count} ready parts · ${total_size}`}
          />
          <p className="dl-status visually-hidden" role="status" aria-live="polite">
            {flow.announcement}
          </p>
          <div>
            <div className="list-head">
              <span>Individual parts</span>
              <span>
                {ready_part_count} ready &middot; {total_size}
              </span>
            </div>
            <ul
              className="parts short"
              aria-label={`Download parts for ${channel_name}`}
              aria-busy={flow.allState === "loading" ? "true" : undefined}
            >
              {parts.map(renderPartRow)}
            </ul>
          </div>
        </div>
      </div>
    );
  }

  // ---- multi (default): N ready parts ----
  return (
    <div className="card card-enter">
      <div className="card-pad">
        <StatusEyebrow dot="ok" tone="ok" text="Ready to download" />
        <div>
          <h1>{channel_name}</h1>
          <p className="sub">
            Your gallery is packaged and ready. It comes in{" "}
            <b>{part_count} parts</b> &mdash; download each one below.
          </p>
        </div>
        <div className="meta">
          <span className="chip">
            <TrayDownloadIcon size={13} />
            {total_size} total
          </span>
          <span className="chip">
            <GridIcon size={13} />
            {part_count} parts
          </span>
          <span className="chip">
            <ClockIcon size={13} />
            Available until {formatExpiry(expires_at)}
          </span>
        </div>
        <DownloadButton
          as="button"
          type="button"
          variant="primary"
          className="js-dl js-dl-all"
          state={flow.allState}
          onClick={handleAll}
          aria-label={`Download all ${part_count} parts, ${spokenSize(total_size)} total`}
          label={`Download all ${part_count} parts · ${total_size}`}
        />
        <p className="dl-status visually-hidden" role="status" aria-live="polite">
          {flow.announcement}
        </p>
        <div>
          <div className="list-head">
            <span>Individual parts</span>
            <span>
              {part_count} files &middot; {total_size}
            </span>
          </div>
          <ul
            className="parts"
            aria-label={`Download parts for ${channel_name}`}
            aria-busy={flow.allState === "loading" ? "true" : undefined}
          >
            {parts.map(renderPartRow)}
          </ul>
        </div>
        <div className="card-note">
          <LockIcon size={16} />
          <span>
            Each part is a secure <code style={{ fontFamily: "var(--mono)" }}>.zip</code>.
            Download links are generated fresh on every click and stay valid for
            90 days &mdash; no rush.
          </span>
        </div>
      </div>
    </div>
  );
};

export default ReadyView;
